import {
  FormControl,
  TextField,
  Typography,
  TextFieldProps,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useState } from "react";
import { APP_COLORS, APP_FONT_FAMILIES } from "../../../enums";
import { FormHelperTextError } from "../errors";

interface FormTextFieldProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  textFieldProps?: TextFieldProps;
  formControlProps?: any;
  labelProps?: any;
}

function FormTextField<T extends FieldValues>({
  name,
  control,
  label,
  required = true,
  error,
  helperText,
  textFieldProps = {},
  formControlProps = {},
  labelProps = {},
}: FormTextFieldProps<T>) {
  const fieldId = `form-field-${name}`;
  const errorId = `component-error-text-${name}`;
  const isPasswordField = String(name).toLowerCase().includes("password");
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FormControl
      variant="outlined"
      margin="dense"
      fullWidth
      {...formControlProps}
    >
      <Typography
        variant="body"
        component="span"
        fontWeight={600}
        fontFamily={APP_FONT_FAMILIES.ibmPlexMono}
        color={APP_COLORS.textFieldLabelBlack}
        mb={0.5}
        sx={{ ...labelProps?.sx }}
      >
        {label}
        {required && <Typography component="span" color={APP_COLORS.error}>*</Typography>}
      </Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            id={fieldId}
            hiddenLabel
            variant="outlined"
            size="small"
            fullWidth
            autoComplete="off"
            error={!!error}
            aria-describedby={errorId}
            {...textFieldProps}
            type={
              isPasswordField
                ? showPassword
                  ? "text"
                  : "password"
                : textFieldProps?.type ?? "text"
            }
            InputProps={{
              ...textFieldProps?.InputProps,
              endAdornment: isPasswordField ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((prev) => !prev)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ) : (
                textFieldProps?.InputProps?.endAdornment
              ),
            }}
          />
        )}
      />
      <FormHelperTextError
        id={errorId}
        error={error}
        helperText={helperText}
      />
    </FormControl>
  );
}

export { FormTextField };
